import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FiSearch, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import axios from 'axios';
import ConfirmationModal from '../components/confirmation';  // Adjust path as needed
import './credit.css';

function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
}

const SearchInput = React.memo(
  React.forwardRef(({ value, onChange }, ref) => (
    <div className="search-input-container">
      <FiSearch className="search-icon" />
      <input
        ref={ref}
        type="text"
        placeholder="Search by customer name"
        value={value}
        onChange={onChange}
        autoComplete="off"
        className="search-input"
      />
    </div>
  ))
);

const CreditList = React.memo(({ 
  searchTerm, 
  currentPage,
  onPageChange,
  totalPages,
  itemsPerPage,
  refreshKey,
  onRequestMarkPaid
}) => {
  const [credits, setCredits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalCredit, setTotalCredit] = useState(0);
  const [totalItems, setTotalItems] = useState(0);

  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  const fetchCreditData = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.get('/sales/credit', { 
        params: { 
          search: debouncedSearchTerm,
          page: currentPage,
          limit: itemsPerPage
        } 
      });
      
      setCredits(response.data.data);
      setTotalItems(response.data.pagination.total_items);
      
      const total = response.data.data.reduce(
        (sum, credit) => sum + parseFloat(credit.credit_amount), 
        0
      );
      setTotalCredit(total);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to fetch credit data');
    } finally {
      setLoading(false);
    }
  }, [debouncedSearchTerm, currentPage, itemsPerPage, refreshKey]);

  useEffect(() => {
    fetchCreditData();
  }, [fetchCreditData]);

  if (loading) return <div className="loading">Loading credit data...</div>;
  if (error) return <div className="error">Error: {error}</div>;
  
  if (credits.length === 0) {
    return (
      <div className="empty-state">
        <p>{debouncedSearchTerm ? 'No credit invoices match your search' : 'No credit invoices found.'}</p>
      </div>
    );
  }

  return (
    <>
      <div className="credit-summary">
        <div className="summary-card">
          <h3>Total Credit Amount</h3>
          <div className="amount">
            Rs. {totalCredit.toLocaleString('en-IN', { 
              minimumFractionDigits: 2, 
              maximumFractionDigits: 2 
            })}
          </div>
        </div>
        <div className="summary-card">
          <h3>Total Credit Invoices</h3>
          <div className="amount">{totalItems}</div>
        </div>
      </div>

      <table className="credit-table">
        <thead>
          <tr>
            <th>Customer Name</th>
            <th>Invoice No</th>
            <th>Credit Amount</th>
            <th>Date</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {credits.map((credit) => (
            <tr key={`${credit.invoice_no}-${credit.created_at}`}>
              <td>{credit.customer_name}</td>
              <td>{credit.invoice_no}</td>
              <td className="amount-cell">
                Rs. {parseFloat(credit.credit_amount).toLocaleString('en-IN', { 
                  minimumFractionDigits: 2, 
                  maximumFractionDigits: 2 
                })}
              </td>
              <td>
                {new Date(credit.created_at).toLocaleDateString('en-IN', {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric'
                })}
              </td>
              <td>
                <button 
                  className="pay-btn"
                  onClick={() => onRequestMarkPaid(credit.invoice_no)}
                >
                  Mark as Paid
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="pagination-controls">
        <button 
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="pagination-btn"
        >
          <FiChevronLeft />
          Previous
        </button>
        
        <span className="page-info">
          Page {currentPage} of {totalPages}
        </span>
        
        <button 
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage >= totalPages}
          className="pagination-btn"
        >
          Next
          <FiChevronRight />
        </button>
      </div>
    </>
  );
});

const CreditPage = () => {
  const inputRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [refreshKey, setRefreshKey] = useState(0);
  const [successMessage, setSuccessMessage] = useState('');


  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [error, setError] = useState(null);

  const [paymentType, setPaymentType] = useState("cash");
  const [bankAccounts, setBankAccounts] = useState([]);
  const [selectedBank, setSelectedBank] = useState("");

  const handleSearchChange = useCallback((e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset page on search
  }, []);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

    useEffect(() => {
    const fetchBankAccounts = async () => {
      try {
        const res = await axios.get("/bank-accounts");
        setBankAccounts(res.data);

        if (res.data.length > 0) {
          setSelectedBank(res.data[0].id);
        }
      } catch (err) {
        console.error(err);
      }
    };

    fetchBankAccounts();
  }, []);

    const handleRequestMarkPaid = (invoiceNo) => {
        setSelectedInvoice(invoiceNo);
    
        setPaymentType("cash");
    
        if (bankAccounts.length > 0) {
            setSelectedBank(bankAccounts[0].id);
        }
      
        setShowConfirmModal(true);
    };

  const handleConfirmMarkPaid = async () => {
    setProcessingPayment(true);
    setError(null);
    setSuccessMessage("");

    try {
          const response = await axios.put(
        `/sales/${selectedInvoice}/mark-paid`,
        {
            payment_type: paymentType,
            bank_account_id:
                paymentType === "bank"
                    ? selectedBank
                    : null
        }
    );

      setSuccessMessage(
        `Invoice ${selectedInvoice} marked as paid (${paymentType.toUpperCase()}).`
      );

      setShowConfirmModal(false);
      setSelectedInvoice(null);

      setRefreshKey(prev => prev + 1);

      setTimeout(() => {
        setSuccessMessage("");
      }, 3000);

    } catch (err) {

      setError(
        err.response?.data?.error ||
        err.message ||
        "Failed to mark invoice as paid"
      );

    } finally {
      setProcessingPayment(false);
    }
  };

  const handleCancelMarkPaid = () => {
    setShowConfirmModal(false);
    setSelectedInvoice(null);
  };

  return (
    <div className="credit-container">
      <div className="credit-header">
        <h1 className="credit-title">Credit Invoices</h1>
        <div className="credit-controls">
          <SearchInput
            value={searchTerm}
            onChange={handleSearchChange}
            ref={inputRef}
          />
        </div>
      </div>

      <CreditList 
        searchTerm={searchTerm}
        currentPage={currentPage}
        onPageChange={handlePageChange}
        totalPages={totalPages}
        itemsPerPage={itemsPerPage}
        refreshKey={refreshKey}
        onRequestMarkPaid={handleRequestMarkPaid}
      />

     {successMessage && (
        <div className="success-message">
          {successMessage}
        </div>
      )}

      {error && <div className="error">{error}</div>}

     {showConfirmModal && (
  <div className="payment-modal-overlay">

    <div className="payment-modal">

      <h2>Mark Invoice Paid</h2>

      <p>
        Invoice <strong>{selectedInvoice}</strong>
      </p>

<div className="payment-options">

    <label className="payment-option">

        <input
            type="radio"
            value="cash"
            checked={paymentType === "cash"}
            onChange={(e) => setPaymentType(e.target.value)}
        />

        Paid as Cash

    </label>

    <label className="payment-option">

        <input
            type="radio"
            value="bank"
            checked={paymentType === "bank"}
            onChange={(e) => setPaymentType(e.target.value)}
        />

        Paid in Bank

    </label>

    {paymentType === "bank" && (

        <div className="bank-select-container">

            <label>Select Bank Account</label>

            <select
                value={selectedBank}
                onChange={(e) => setSelectedBank(Number(e.target.value))}
                className="bank-select"
            >

                {bankAccounts.map((bank) => (

                    <option
                        key={bank.id}
                        value={bank.id}
                    >
                        {bank.account_name}
                        {" - "}
                        {bank.account_number}
                    </option>

                ))}

            </select>

        </div>

    )}

</div>

      <div className="payment-modal-buttons">

        <button
          className="cancel-btn"
          onClick={handleCancelMarkPaid}
          disabled={processingPayment}
        >
          Cancel
        </button>

        <button
          className="confirm-btn"
          onClick={handleConfirmMarkPaid}
          disabled={processingPayment}
        >
          {processingPayment ? "Processing..." : "Confirm"}
        </button>

      </div>

    </div>

  </div>
)}
    
    </div>
  );
};

export default CreditPage;
